import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ArrowUp } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Footer = ({ onNavigate }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { id: 'home', label: 'Home' },
    { id: 'services', label: 'Services' },
    { id: 'about', label: 'About Us' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'faq', label: 'FAQ', link: '/faq' },
    { id: 'contact', label: 'Contact' }
  ];

  const services = [
    'AC Tune-Up',
    'Heating Maintenance',
    'Thermostat Installation',
    'Emergency Services',
    'Commercial HVAC',
    'Air Duct Cleaning'
  ];

  return (
    <footer className="bg-[#622B62] text-white" itemScope itemType="https://schema.org/LocalBusiness">
      {/* Hidden NAP for SEO */}
      <meta itemProp="name" content="Romano Heating & Cooling" />
      <meta itemProp="telephone" content="(916) 274-9021" />
      <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress" style={{display: 'none'}}>
        <span itemProp="streetAddress">1316 Church Street</span>
        <span itemProp="addressLocality">Roseville</span>
        <span itemProp="addressRegion">CA</span>
        <span itemProp="postalCode">95678</span>
      </div>
      
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img
                src={companyInfo.logo}
                alt="Romano Heating & Cooling"
                className="h-32 w-32 object-contain rounded-lg"
                itemProp="logo"
              />
              <div>
                <h3 className="font-bold text-lg romano-heading">Romano Heating & Cooling</h3>
                <p className="text-white/70 text-sm">Since 2005</p>
              </div>
            </div>
            <p className="text-white/80 mb-6 leading-relaxed">
              Your trusted HVAC partner since 2005. Providing quality heating and
              cooling services to Roseville, Rocklin, Folsom, El Dorado & surrounding areas.
            </p>
            <div className="flex gap-3">
              <a
                href={companyInfo.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); window.open(companyInfo.socialLinks.facebook, '_blank') || (window.location.href = companyInfo.socialLinks.facebook); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#DA9C3A] flex items-center justify-center transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href={companyInfo.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); window.open(companyInfo.socialLinks.instagram, '_blank') || (window.location.href = companyInfo.socialLinks.instagram); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#DA9C3A] flex items-center justify-center transition-colors"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  {link.link ? (
                    <Link
                      to={link.link}
                      className="text-white/80 hover:text-[#DA9C3A] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => onNavigate(link.id)}
                      className="text-white/80 hover:text-[#DA9C3A] transition-colors"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <button
                    onClick={() => onNavigate('services')}
                    className="text-white/80 hover:text-[#DA9C3A] transition-colors"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - NAP */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:9162749021"
                  className="flex items-center gap-3 text-white/80 hover:text-[#DA9C3A] transition-colors"
                  itemProp="telephone"
                >
                  <Phone size={18} className="text-[#DA9C3A]" />
                  (916) 274-9021
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@romanoheatingandcooling.com"
                  className="flex items-center gap-3 text-white/80 hover:text-[#DA9C3A] transition-colors"
                  itemProp="email"
                >
                  <Mail size={18} className="text-[#DA9C3A]" />
                  info@romanoheatingandcooling.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#DA9C3A] flex-shrink-0 mt-1" />
                <span className="text-white/80">
                  1316 Church Street, Roseville, CA 95678
                </span>
              </li>
            </ul>

            {/* Newsletter Signup Teaser */}
            <div className="mt-6 p-4 bg-white/10 rounded-xl">
              <p className="text-sm text-[#DA9C3A] font-semibold mb-1">Special Offer!</p>
              <p className="text-sm text-white/80">
                Get 10% off your first service when you call today!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} Romano Heating & Cooling. All Rights Reserved. | License #1077499
            </p>
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-sm">Proudly serving the community since 2005</span>
              <button
                onClick={scrollToTop}
                className="w-10 h-10 rounded-full bg-[#DA9C3A] hover:bg-[#C48A2A] flex items-center justify-center transition-colors shadow-lg"
              >
                <ArrowUp size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
